
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Search, Filter, X, Calendar as CalendarIcon, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

interface SearchFiltersProps {
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  interviewFilter: string;
  setInterviewFilter: (value: string) => void;
  dateFilter: string;
  setDateFilter: (value: string) => void;
  timeFilter: string;
  setTimeFilter: (value: string) => void;
  dateRange: { from?: Date; to?: Date };
  setDateRange: (range: { from?: Date; to?: Date }) => void;
  hasActiveFilters: boolean;
  clearFilters: () => void;
  filteredCount: number;
  totalCount: number;
}

export const SearchFilters = ({
  searchTerm,
  setSearchTerm,
  interviewFilter,
  setInterviewFilter,
  dateFilter,
  setDateFilter,
  timeFilter,
  setTimeFilter,
  dateRange,
  setDateRange,
  hasActiveFilters,
  clearFilters,
  filteredCount,
  totalCount
}: SearchFiltersProps) => {
  return (
    <Card className="mb-6 bg-white shadow-sm">
      <CardHeader className="bg-white">
        <CardTitle className="flex items-center gap-2 text-gray-900">
          <Filter className="h-5 w-5" />
          Search & Filter
        </CardTitle>
      </CardHeader>
      <CardContent className="bg-white">
        <div className="flex flex-col gap-4">
          {/* First Row: Search and Interview Filter */}
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search by name, email, or any text..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white"
              />
            </div>

            {/* Interview Filter */}
            <Select value={interviewFilter} onValueChange={setInterviewFilter}>
              <SelectTrigger className="w-full md:w-48 bg-white">
                <SelectValue placeholder="Interview Interest" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Interviews</SelectItem>
                <SelectItem value="interested">Interested</SelectItem>
                <SelectItem value="not-interested">Not Interested</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Second Row: Date and Time Filters */}
          <div className="flex flex-col md:flex-row gap-4">
            {/* Quick Date Filter */}
            <Select value={dateFilter} onValueChange={setDateFilter}>
              <SelectTrigger className="w-full md:w-40 bg-white">
                <SelectValue placeholder="Quick Dates" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="yesterday">Yesterday</SelectItem>
                <SelectItem value="week">This Week</SelectItem>
                <SelectItem value="month">This Month</SelectItem>
              </SelectContent>
            </Select>

            {/* Date Range Picker */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full md:w-64 justify-start text-left font-normal bg-white",
                    !dateRange.from && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateRange.from ? (
                    dateRange.to ? (
                      `${format(dateRange.from, "dd/MM/yy")} - ${format(dateRange.to, "dd/MM/yy")}`
                    ) : (
                      format(dateRange.from, "dd/MM/yyyy")
                    )
                  ) : (
                    "Pick date range"
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-white" align="start">
                <Calendar
                  initialFocus
                  mode="range"
                  defaultMonth={dateRange.from}
                  selected={{ from: dateRange.from, to: dateRange.to }}
                  onSelect={(range) => setDateRange(range || {})}
                  numberOfMonths={2}
                  className="bg-white"
                />
              </PopoverContent>
            </Popover>

            {/* Time Filter */}
            <Select value={timeFilter} onValueChange={setTimeFilter}>
              <SelectTrigger className="w-full md:w-44 bg-white">
                <Clock className="mr-2 h-4 w-4" />
                <SelectValue placeholder="Time Range" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Hours</SelectItem>
                <SelectItem value="dawn">Dawn (00:00-06:00)</SelectItem>
                <SelectItem value="morning">Morning (06:00-12:00)</SelectItem>
                <SelectItem value="afternoon">Afternoon (12:00-18:00)</SelectItem>
                <SelectItem value="evening">Evening (18:00-24:00)</SelectItem>
              </SelectContent>
            </Select>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button 
                onClick={clearFilters} 
                variant="outline" 
                size="icon"
                className="shrink-0 bg-white"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Results Counter */}
        <div className="mt-4 text-sm text-gray-600">
          Showing {filteredCount} of {totalCount} records
          {hasActiveFilters && (
            <span className="ml-2 text-blue-600 font-medium">
              (filtered)
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
